import { useEffect } from "react";
import { Box, Button, Stack, Text } from "@mantine/core";
import { useGo } from "../components/ConsoleShell";
import { Cursor, EmptyState, LoadError, Prompt, Screen } from "../components/primitives";
import { useConsoleStore } from "../stores/consoleStore";
import { useQueryStore } from "../stores/queryStore";
import { useWorkspaceStore } from "../stores/workspaceStore";
import css from "./screens.module.css";

export default function MultiQuery() {
  const go = useGo();
  const workspaceId = useWorkspaceStore((s) => s.currentId);
  const namespaces = useConsoleStore((s) => s.namespaces);
  const loadError = useConsoleStore((s) => s.errors.namespaces);
  const loadNamespaces = useConsoleStore((s) => s.loadNamespaces);

  const {
    selected, multiText, multiRunning, multiResult, multiError,
    toggleSelected, setSelected, setMultiText, runMulti,
  } = useQueryStore();

  useEffect(() => {
    if (workspaceId && namespaces.length === 0) void loadNamespaces();
  }, [workspaceId, namespaces.length, loadNamespaces]);

  // The mockup opens with support-kb, blog-articles and agent-memory selected;
  // fall back to the first three when those namespaces don't exist.
  useEffect(() => {
    if (selected.length > 0 || namespaces.length === 0) return;
    const preferred = ["support-kb", "blog-articles", "agent-memory"];
    const ids = namespaces.map((n) => n.id);
    const picked = preferred.filter((p) => ids.includes(p));
    setSelected(picked.length > 0 ? picked : ids.slice(0, 3));
  }, [selected.length, namespaces, setSelected]);

  if (namespaces.length === 0) {
    return (
      <Screen>
        <Prompt command="vf query --multi" comment="# fan-out across namespaces, merge by global score" />
        <LoadError message={loadError} />
        <EmptyState
          title="no namespaces to fan out across"
          body="Multi-query merges results from several namespaces into one ranked list. Create at least one namespace to try it."
          action={
            <Button size="md" fz="13.5px" fw={600} onClick={() => go("/namespaces/new")}>
              ▸ create a namespace
            </Button>
          }
        />
      </Screen>
    );
  }

  return (
    <Screen>
      <Prompt
        command="vf query --multi"
        comment="# fan-out across namespaces, merge by global score"
      />

      <Box className={css.splitSidebar}>
        <Box p="15px 16px" style={{ border: "1px solid var(--vf-border)", background: "var(--vf-panel)" }}>
          <div className={css.fieldLabel}>
            // target namespaces <span style={{ color: "var(--vf-acc)" }}>({selected.length})</span>
          </div>
          <Stack gap={2} mb={16}>
            {namespaces.map((n) => {
              const on = selected.includes(n.id);
              return (
                <button
                  key={n.id}
                  type="button"
                  role="checkbox"
                  aria-checked={on}
                  className={css.nsPicker}
                  onClick={() => toggleSelected(n.id)}
                >
                  <span style={{ color: on ? "var(--vf-ok)" : "var(--vf-dim)" }}>
                    {on ? "[x]" : "[ ]"}
                  </span>
                  <span
                    style={{
                      color: "var(--vf-text)",
                      flex: 1,
                      whiteSpace: "nowrap",
                      overflow: "hidden",
                      textOverflow: "ellipsis",
                    }}
                  >
                    {n.label}
                  </span>
                </button>
              );
            })}
          </Stack>

          <div className={css.fieldLabel}>// query</div>
          <textarea
            className={css.queryTextarea}
            style={{ width: "100%", marginBottom: 14, fontSize: 13 }}
            rows={2}
            value={multiText}
            aria-label="multi-namespace query"
            onChange={(e) => setMultiText(e.currentTarget.value)}
          />

          {multiRunning ? (
            <button type="button" className={css.primaryRunning} disabled>
              searching {selected.length} namespaces
              <Cursor />
            </button>
          ) : selected.length > 0 ? (
            <button type="button" className={css.primaryWide} onClick={() => void runMulti()}>
              ▸ fan-out search
            </button>
          ) : (
            <button type="button" className={css.primaryDisabled} disabled>
              ▸ select a namespace
            </button>
          )}
        </Box>

        <Box>
          {multiError ? (
            <Text fz="12.5px" c="var(--vf-bad)">
              ✗ {multiError}
            </Text>
          ) : null}

          {multiResult ? (
            <>
              <Text fz="12.5px" c="var(--vf-ok)" mb={10}>
                ✓ merged {multiResult.hits.length} results across {multiResult.searchedNamespaces}{" "}
                namespaces
              </Text>
              {multiResult.hits.map((hit, i) => (
                <div key={`${hit.namespace}-${hit.id}`} className={css.hitCard}>
                  <div className={css.hitHead}>
                    <span className={css.hitRank}>[{i + 1}]</span>
                    <span className={css.hitId} style={{ maxWidth: 180 }}>
                      {hit.id}
                    </span>
                    <span className={css.nsBadge}>{hit.namespaceLabel ?? hit.namespace}</span>
                    <span style={{ flex: 1 }} />
                    <span className={css.hitScore}>{hit.score.toFixed(3)}</span>
                  </div>
                  <p className={`${css.hitBody}`} style={{ marginBottom: 0 }}>
                    {hit.content}
                  </p>
                </div>
              ))}
            </>
          ) : null}
        </Box>
      </Box>
    </Screen>
  );
}
